import { useSyncExternalStore } from 'react'

// `subscribe` has to be referentially stable or `useSyncExternalStore`
// resubscribes on every render, so the listener wiring is cached per query.
const subscribers = new Map<string, (onChange: () => void) => () => void>()

function subscriberFor(query: string) {
  let subscribe = subscribers.get(query)
  if (!subscribe) {
    subscribe = (onChange: () => void) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    }
    subscribers.set(query, subscribe)
  }
  return subscribe
}

/**
 * SSR-safe media query. The server snapshot is always `false`, so the first
 * client render matches the markup React streamed and the real value lands in
 * the commit right after — no hydration mismatch, and no debounce lag on
 * orientation change.
 */
export function useMediaQuery(query: string) {
  return useSyncExternalStore(
    subscriberFor(query),
    () => window.matchMedia(query).matches,
    () => false,
  )
}

/** Phone-width layouts. The site-wide breakpoint; matches Tailwind's `md`. */
export function useIsMobile() {
  return useMediaQuery('(max-width: 767.98px)')
}

/**
 * A handheld, in either orientation.
 *
 * Width alone gets this wrong twice over: a phone on its side is wider than the
 * `md` breakpoint, and an iPad in portrait is narrower than plenty of laptops.
 * Testing both axes is what separates them — a phone is under 600 px on its
 * short edge whichever way up it is, and no tablet is.
 *
 * `/mac` gives this the room framed on the machine with a tap to go in, rather
 * than a room there is no screen to look around. A tablet is not a phone by
 * this test and gets the whole room — touch is a reason to give the room's
 * hover gestures an equivalent, not a reason to withhold it from a device well
 * able to draw it.
 */
export function useIsPhone() {
  return useMediaQuery(
    '(pointer: coarse) and ((max-width: 599.98px) or (max-height: 599.98px))',
  )
}
